'use client';

import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ExpandableCallout, Text, TextInput } from '@galapagos/design-system/web';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { PrivacyNote } from '../components/PrivacyNote';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupIdentityStyle } from '../components/signup-identity-style';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useStepHeading } from '../hooks/useStepHeading';
import {
  LEGAL_NAME_MAX_LENGTH,
  legalNameProblem,
  type LegalNameProblem,
} from '../services/identity-fields';
import { sameSponsor, useSignupFlowStore } from '../stores/signup-flow-store';
import { EarlierAnswersGate } from './EarlierAnswersGate';

const FIRST_NAME_ID = 'signup-legal-first-name';
const LAST_NAME_ID = 'signup-legal-last-name';

function LegalNameStep() {
  // Every flow page refills the sponsor params from its own URL.
  const linkParams = useLegacySignupParams();
  const storedParams = useSignupFlowStore((state) => state.params);
  const { t } = useTranslation('signup');
  const title = t('legalName.title');
  const headingRef = useStepHeading(title);
  const { goToNextStep } = useSignupNavigation();
  // A saved name pre-fills only for the same sponsor's link: a different one
  // is a new flow, and the store clears the name once it reads the new link.
  const savedName = useSignupFlowStore((state) => state.legalName);
  const stored = storedParams === null || sameSponsor(storedParams, linkParams) ? savedName : null;
  const setLegalName = useSignupFlowStore((state) => state.setLegalName);
  const [firstName, setFirstName] = useState(stored?.firstName ?? '');
  const [lastName, setLastName] = useState(stored?.lastName ?? '');
  const [problems, setProblems] = useState<{
    first: LegalNameProblem | null;
    last: LegalNameProblem | null;
  }>({ first: null, last: null });
  const formRef = useRef<HTMLFormElement>(null);
  ensureSignupStepStyle();
  ensureSignupIdentityStyle();

  const problemText = (problem: LegalNameProblem | null, missing: string): string | undefined => {
    if (problem === 'missing') return missing;
    if (problem === 'tooLong') return t('legalName.tooLongError', { max: LEGAL_NAME_MAX_LENGTH });
    return undefined;
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const first = firstName.trim();
    const last = lastName.trim();
    const next = { first: legalNameProblem(first), last: legalNameProblem(last) };
    setProblems(next);
    if (next.first || next.last) {
      const invalidId = next.first ? FIRST_NAME_ID : LAST_NAME_ID;
      formRef.current?.querySelector<HTMLInputElement>(`[data-testid="${invalidId}"]`)?.focus();
      return;
    }
    setLegalName({ firstName: first, lastName: last });
    goToNextStep('legal-name');
  };

  return (
    <SignupShell>
      <form ref={formRef} className="signup-step" noValidate onSubmit={onSubmit}>
        <PageTitle title={title} headingRef={headingRef} />
        <ExpandableCallout
          title={t('legalName.whyTitle')}
          actionId="signup.legal-name-screen-why.toggle"
          data-testid="signup-legal-name-why"
        >
          <Text variant="b3Regular" value={t('legalName.whyBody')} />
        </ExpandableCallout>
        {/* Member-entered values: kept out of autocapture and click events. */}
        <div className="ph-no-capture signup-fields">
          <TextInput
            label={t('legalName.firstNameLabel')}
            value={firstName}
            onChangeText={setFirstName}
            autoComplete="given-name"
            name="given-name"
            errorText={problemText(problems.first, t('legalName.firstNameError'))}
            data-testid={FIRST_NAME_ID}
          />
          <TextInput
            label={t('legalName.lastNameLabel')}
            value={lastName}
            onChangeText={setLastName}
            autoComplete="family-name"
            name="family-name"
            errorText={problemText(problems.last, t('legalName.lastNameError'))}
            data-testid={LAST_NAME_ID}
          />
        </div>
        <PrivacyNote text={t('legalName.privacyNote')} />
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            htmlType="submit"
            actionId="signup.legal-name-screen-continue.activate"
            data-testid="signup-continue"
          />
        </FormActions>
      </form>
    </SignupShell>
  );
}

/** `/legal-name/`: the member's legal name, for the eligibility check (v9 1.3). */
export function LegalNameStepScreen() {
  return (
    <EarlierAnswersGate step="legal-name">
      <LegalNameStep />
    </EarlierAnswersGate>
  );
}
